import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import numeral from 'numeral';
import { Badge, Table } from 'react-bootstrap';

import { AgGridReact } from '@ag-grid-community/react';
import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-alpine.css';
import { ModuleRegistry } from '@ag-grid-community/core';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { CsvExportModule } from '@ag-grid-community/csv-export';

import './styles.css';

import { ColumnsToolPanelModule } from '@ag-grid-enterprise/column-tool-panel';
import { FiltersToolPanelModule } from '@ag-grid-enterprise/filter-tool-panel';
import { SetFilterModule } from '@ag-grid-enterprise/set-filter';
import { RowGroupingModule } from '@ag-grid-enterprise/row-grouping';
import { ExcelExportModule } from '@ag-grid-enterprise/excel-export';
import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model';

import axios from '../../../../utils/axios';
import { AG_GRID_LOCALE_MS } from '../../../../locales/locale';

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ServerSideRowModelModule,
  ColumnsToolPanelModule,
  RowGroupingModule,
  CsvExportModule,
  ExcelExportModule,
  FiltersToolPanelModule,
  SetFilterModule,
]);

// Feature flag: server-side row model (Stage 1).
// Disables row grouping, set filters, and totals row while enabled.
// Set to false to fall back to the original client-side behaviour.
const USE_SERVER_SIDE = true;

// Memoized cell renderer components to prevent re-renders
const StatusBadgeRenderer = React.memo(({ data }) => {
  if (!data?.current_progress_status) return null;
  return (
    <Badge bg="" style={{ backgroundColor: data.current_progress_status.color }}>
      {data.current_progress_status.label}
    </Badge>
  );
});

const ScopeTableRenderer = React.memo(({ data }) => {
  if (!data?.financial_details?.length) return null;
  return (
    <Table size="sm" bordered className="mb-0 pb-0">
      <thead className="text-uppercase" style={{ fontSize: 12 }}>
        <tr>
          <th>Skop Projek</th>
          <th>Siling (RM)</th>
        </tr>
      </thead>
      <tbody>
        {data.financial_details.map((item, index) => (
          <tr key={item.id || index}>
            <td width={300}>{item.cost_description}</td>
            <td className="text-end">{numeral(item.ceiling).format('0,0.00')}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
});

const LocationRenderer = React.memo(({ data, field }) => {
  const site = data?.site_information;
  if (!site) return null;

  switch (field) {
    case 'daerah':
      return <div className="d-flex justify-content-center">{site.daerah?.name}</div>;
    case 'parliament':
      return (
        <div className="d-flex justify-content-center">
          {site.parliament?.name} - {site.parliament?.code}
        </div>
      );
    case 'dun':
      return (
        <div className="d-flex justify-content-center">
          {site.state_legislative_assembly?.name} - {site.state_legislative_assembly?.code}
        </div>
      );
    default:
      return null;
  }
});

// Shared formatter for number columns — keeps the cell value numeric
// (so AG Grid treats it as a number, sorts/exports correctly) while still
// rendering the user-facing thousands separator + 2 decimals.
const moneyFormatter = (params) => {
  if (params.value === '' || params.value == null) return '';
  const n = typeof params.value === 'number' ? params.value : Number(params.value);
  if (!Number.isFinite(n)) return '';
  return numeral(n).format('0,0.00');
};

// Helper functions moved outside component to prevent recreation
// Newest SENT laporan kemajuan (year, then month, then sent time).
const getLatestReport = (reports) => {
  if (!reports?.length) return null;
  return reports
    .filter((item) => item.is_sent === true)
    .sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      if (a.month !== b.month) return b.month - a.month;
      return new Date(b.sent_at) - new Date(a.sent_at);
    })[0];
};

const getLatestReportProgress = (reports) => getLatestReport(reports)?.progress || 0;

// "08/2026" for the latest sent report; '' when there is none.
// Catatan of the latest sent report as plain text — the editor stores HTML
// (paragraphs and lists), so tags are dropped and blocks become line breaks.
const getLatestReportNotes = (reports) => {
  const html = getLatestReport(reports)?.notes;
  if (!html) return '';
  const withBreaks = String(html).replace(/<\/(p|li|div)>|<br\s*\/?>/gi, '\n');
  const text = new DOMParser().parseFromString(withBreaks, 'text/html').body.textContent || '';
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .join('\n');
};

const NotesRenderer = React.memo(({ value }) => {
  if (!value) return null;
  // pre-line keeps the catatan's own line breaks and wraps long lines.
  return <div style={{ whiteSpace: 'pre-line', lineHeight: 1.4, padding: '6px 0' }}>{value}</div>;
});

const getLatestReportMonth = (reports) => {
  const latest = getLatestReport(reports);
  if (!latest?.year) return '';
  const month = Number(latest.month);
  return month >= 1 && month <= 12
    ? `${String(month).padStart(2, '0')}/${latest.year}`
    : String(latest.year);
};

const calculateCeilingByYear = (financialDetails, year) => {
  if (!financialDetails?.length || !year) return 0;
  return financialDetails
    .flatMap((obj) => obj.ceiling_flow_details || [])
    .filter((obj) => obj.year === year)
    .reduce((acc, obj) => acc + parseFloat(obj.amount || 0), 0);
};

const calculateRevisedCeilingByYear = (financialDetails, year) => {
  if (!financialDetails?.length || !year) return 0;
  return financialDetails
    .flatMap((obj) => obj.ceiling_flow_details || [])
    .filter((obj) => obj.year === year)
    .reduce((acc, obj) => acc + parseFloat(obj.revised_amount || 0), 0);
};

const getS3pDataByYear = (s3pData, year) => {
  if (!s3pData?.length || !year) return null;
  return s3pData.find((item) => item.year === year);
};

const calculateMonthlyTotal = (monthlyData, field) => {
  if (!monthlyData?.length) return 0;
  return monthlyData.reduce((acc, item) => acc + parseFloat(item[field] || 0), 0);
};

const AnalysisTable = ({
  gridRef,
  onGridReady,
  rowData,
  setRowData,
  selectRmk,
  saveColumnState,
  scopeFilter,
}) => {
  const containerStyle = useMemo(() => ({ width: '100%', height: '100%' }), []);
  const gridStyle = useMemo(() => ({ height: '85vh', width: '100%' }), []);

  // Debounced save column state to prevent excessive localStorage writes
  const debouncedSaveColumnState = useCallback(() => {
    if (saveColumnState) {
      // Use requestIdleCallback for non-critical updates
      if (window.requestIdleCallback) {
        window.requestIdleCallback(saveColumnState);
      } else {
        setTimeout(saveColumnState, 100);
      }
    }
  }, [saveColumnState]);

  // Server-side row model: only certain columns have a backend implementation
  // for set-filter values + grouping. Anything outside this whitelist falls
  // back to text filters and has grouping disabled.
  const SERVER_SIDE_SUPPORTED = useMemo(
    () => new Set(['ministry', 'department', 'branch', 'program', 'main_project']),
    []
  );

  // Async values loader for agSetColumnFilter — calls the filter-values endpoint
  // for the column id. Wrapped per colId so AG Grid memoises it stably.
  const buildSetFilterValuesLoader = useCallback(
    (colId) => (params) => {
      const isFederalProjects = JSON.parse(localStorage.getItem('federalProjects'))?.value || false;
      axios
        .get('/project-analysis-table/filter-values', {
          params: {
            col: colId,
            rmk_id: selectRmk?.number,
            is_federal_projects: isFederalProjects,
          },
        })
        .then(({ data }) => {
          const payload = data?.data ?? data;
          params.success(payload?.values ?? []);
        })
        .catch(() => params.success([]));
    },
    [selectRmk]
  );

  const adaptColDefForServerSide = useCallback(
    (col) => {
      if (!USE_SERVER_SIDE) return col;
      const next = { ...col };
      const supported = SERVER_SIDE_SUPPORTED.has(next.colId);
      if (next.enableRowGroup && !supported) {
        delete next.enableRowGroup;
      }
      if (next.filter === 'agSetColumnFilter') {
        if (supported) {
          // Restore set filter with async values from the server.
          next.filterParams = {
            ...(next.filterParams || {}),
            values: buildSetFilterValuesLoader(next.colId),
          };
        } else {
          next.filter = 'agTextColumnFilter';
        }
      }
      return next;
    },
    [SERVER_SIDE_SUPPORTED, buildSetFilterValuesLoader]
  );

  const colDefs = useMemo(() => {
    if (!selectRmk) return [];

    const { first_year, second_year, third_year, fourth_year, fifth_year, label } = selectRmk;

    return [
      {
        headerName: '#',
        field: 'row_number',
        width: 60,
        suppressSizeToFit: true,
        valueGetter: (params) => {
          if (params.data?._isGrandTotal) return 'JUMLAH';
          if (params.node?.footer) return '';
          if (params.node?.group) return params.data?._groupCount ?? '';
          return params.data?.row_number ?? (params.node?.rowIndex ?? 0) + 1;
        },
      },
      {
        colId: 'parent_project',
        headerName: 'SAMBUNGAN',
        field: 'parent_project',
        width: 130,
        suppressSizeToFit: true,
      },
      {
        colId: 'rmk',
        headerName: 'RMK',
        field: 'rmk.number',
        width: 80,
        suppressSizeToFit: true,
      },
      {
        colId: 'ministry',
        headerName: 'KEMENTERIAN',
        enableRowGroup: true,
        field: 'ministry.short_form',
        sortable: true,
        filter: 'agSetColumnFilter',
        suppressFiltersToolPanel: true,
      },
      {
        colId: 'department',
        headerName: 'JABATAN',
        enableRowGroup: true,
        field: 'executing_agency.short_form',
        sortable: true,
        filter: 'agSetColumnFilter',
        suppressFiltersToolPanel: true,
      },
      {
        colId: 'branch',
        headerName: 'CAWANGAN',
        enableRowGroup: true,
        field: 'branch.name',
        sortable: true,
        filter: 'agSetColumnFilter',
        suppressFiltersToolPanel: true,
      },
      {
        headerName: 'BUTIRAN KEMENTERIAN',
        field: 'ministry.code',
        filter: 'agSetColumnFilter',
      },
      {
        colId: 'program',
        headerName: 'NAMA PROGRAM',
        field: 'program.name',
        enableRowGroup: true,
        filter: 'agSetColumnFilter',
      },
      {
        headerName: 'BUTIRAN PROGRAM',
        field: 'program.code',
        enableRowGroup: true,
        filter: 'agSetColumnFilter',
      },
      {
        colId: 'main_project',
        headerName: 'NAMA PROJEK UTAMA',
        field: 'main_project.name',
        enableRowGroup: true,
        filter: 'agSetColumnFilter',
      },
      {
        headerName: 'BUTIRAN PROJEK UTAMA',
        field: 'main_project.code',
        enableRowGroup: true,
        filter: 'agSetColumnFilter',
      },
      {
        headerName: 'KOD PROJEK',
        field: 'sub_project_full_code',
        width: 350,
        filter: 'agTextColumnFilter',
      },
      {
        headerName: 'NAMA PROJEK',
        field: 'name',
        width: 500,
        filter: 'agTextColumnFilter',
        // Searched from the box above the table instead of the Saringan panel.
        suppressFiltersToolPanel: true,
      },
      {
        headerName: 'KATEGORI PROJEK',
        field: 'project_category.code',
        enableRowGroup: true,
        filter: 'agSetColumnFilter',
      },
      {
        headerName: 'SEKSYEN UPEN',
        field: 'upen_section.code',
        enableRowGroup: true,
        filter: 'agSetColumnFilter',
      },
      { headerName: 'SDG' },
      {
        headerName: 'SEKTOR',
        field: 'main_sector.name',
        enableRowGroup: true,
        filter: 'agSetColumnFilter',
      },
      {
        headerName: 'JENIS PROJEK',
        field: 'project_type.name',
        enableRowGroup: true,
        filter: 'agSetColumnFilter',
      },
      {
        headerName: 'KETERANGAN PROJEK',
        field: 'description',
        width: 800,
      },
      {
        headerName: 'JUSTIFIKASI',
        field: 'justification',
        width: 800,
      },
      {
        headerName: 'FAEDAH PROJEK',
        suppressToolPanel: true,
        field: 'benefits',
        width: 800,
      },
      { headerName: 'KELUASAN TAPAK' },
      { headerName: 'REKA BENTUK' },
      { headerName: 'TARIKH TENDER' },
      { headerName: 'TARIKH MULA PROJEK' },
      { headerName: 'TARIKH SIAP PROJEK' },
      { headerName: 'TEMPOH PELAKSANAAN' },
      {
        headerName: 'STATUS KEMAJUAN PROJEK',
        field: 'current_progress_status.label',
        width: 230,
        sortable: true,
        filter: 'agSetColumnFilter',
        enableRowGroup: true,
        cellRenderer: StatusBadgeRenderer,
      },
      {
        // Agency's catatan from the same latest sent report as the status.
        headerName: 'CATATAN LAPORAN KEMAJUAN',
        colId: 'report_notes',
        width: 380,
        sortable: false,
        filter: false,
        // Wrap the full catatan; the row grows to fit it.
        wrapText: true,
        autoHeight: true,
        cellClass: 'cell-wrap-text',
        valueGetter: (params) => {
          if (params.node?.group || params.data?._isGrandTotal) return null;
          return getLatestReportNotes(params.data?.reports);
        },
        cellRenderer: NotesRenderer,
      },
      {
        // Month/year of the latest sent laporan kemajuan — the report the
        // status and peratus columns are read from.
        headerName: 'BULAN LAPORAN KEMAJUAN',
        colId: 'report_month',
        width: 200,
        sortable: true,
        filter: false,
        valueGetter: (params) => {
          // Group and grand-total rows have no single report to describe.
          if (params.node?.group || params.data?._isGrandTotal) return null;
          return getLatestReportMonth(params.data?.reports);
        },
        cellClass: 'text-center',
      },
      {
        // Latest sent report's progress as a real numeric value so the column
        // sorts/filters server-side and exports.
        headerName: 'PERATUS KEMAJUAN (%)',
        colId: 'progress',
        width: 190,
        sortable: true,
        filter: 'agNumberColumnFilter',
        valueGetter: (params) => {
          // Blank on group / grand-total rows for now. The server already
          // sends the group average as `_avgProgress` if it is ever wanted.
          if (params.node?.group || params.data?._isGrandTotal) return null;
          return getLatestReportProgress(params.data?.reports);
        },
        valueFormatter: (params) => (params.value == null ? '' : `${params.value}%`),
        cellClass: 'text-end',
      },
      {
        headerName: 'SKOP PROJEK ASAL',
        width: 500,
        cellClass: 'cell-wrap-text',
        cellRenderer: ScopeTableRenderer,
        autoHeight: true,
        wrapText: false,
      },
      { headerName: 'SKOP PROJEK DISEMAK' },
      { headerName: 'MAKLUMAT VO' },
      { headerName: 'MAKLUMAT EOT' },
      { headerName: 'MAKLUMAT KONTRAKTOR' },
      { headerName: 'AGENSI PEMILIK', enableRowGroup: true },
      {
        headerName: 'AGENSI PELAKSANA',
        field: 'executing_agency.name',
        enableRowGroup: true,
      },
      {
        headerName: 'DAERAH',
        enableRowGroup: true,
        cellRenderer: (params) => <LocationRenderer data={params.data} field="daerah" />,
      },
      {
        headerName: 'PARLIMEN',
        enableRowGroup: true,
        cellRenderer: (params) => <LocationRenderer data={params.data} field="parliament" />,
      },
      {
        headerName: 'DUN',
        enableRowGroup: true,
        cellRenderer: (params) => <LocationRenderer data={params.data} field="dun" />,
      },
      { headerName: 'KOORDINAT GIS' },
      { headerName: 'KOS KESELURUHAN' },
      {
        headerName: 'KOS ASAL',
        cellClass: 'ag-right-aligned-cell',
        cellDataType: 'number',
        valueFormatter: moneyFormatter,
        valueGetter: ({ data }) =>
          (data?.financial_details ?? []).reduce(
            (acc, obj) => acc + parseFloat(obj.cost_required || 0),
            0
          ),
        aggFunc: 'jumlah',
      },
      {
        headerName: 'KOS DISEMAK',
        cellClass: 'ag-right-aligned-cell',
        cellDataType: 'number',
        valueFormatter: moneyFormatter,
        valueGetter: ({ data }) =>
          (data?.financial_details ?? []).reduce(
            (acc, obj) => acc + parseFloat(obj.revised_cost_required || 0),
            0
          ),
        aggFunc: 'jumlah',
      },
      {
        headerName: `MAKLUMAT SILING ASAL ${label} (RM)`,
        cellClass: 'ag-right-aligned-cell',
        cellDataType: 'number',
        valueFormatter: moneyFormatter,
        valueGetter: ({ data }) =>
          (data?.financial_details ?? []).reduce(
            (acc, obj) => acc + parseFloat(obj.ceiling || 0),
            0
          ),
        aggFunc: 'jumlah',
      },
      // Year-based columns
      ...[first_year, second_year, third_year, fourth_year, fifth_year].map((year) => ({
        headerName: `SILING ASAL TAHUN ${year} (RM)`,
        suppressSizeToFit: true,
        width: 250,
        cellClass: 'ag-right-aligned-cell',
        cellDataType: 'number',
        valueFormatter: moneyFormatter,
        valueGetter: ({ data }) => calculateCeilingByYear(data?.financial_details, year),
        aggFunc: 'jumlah',
      })),
      ...[first_year, second_year, third_year, fourth_year, fifth_year].map((year) => ({
        headerName: `SILING DIPINDA TAHUN ${year} (RM)`,
        suppressSizeToFit: true,
        width: 300,
        cellClass: 'ag-right-aligned-cell',
        cellDataType: 'number',
        valueFormatter: moneyFormatter,
        valueGetter: ({ data }) => calculateRevisedCeilingByYear(data?.financial_details, year),
        aggFunc: 'jumlah',
      })),
      ...[first_year, second_year, third_year, fourth_year, fifth_year].map((year) => ({
        headerName: `PERUNTUKAN ASAL MOF TAHUN ${year} (RM)`,
        suppressSizeToFit: true,
        width: 350,
        cellClass: 'ag-right-aligned-cell',
        cellDataType: 'number',
        valueFormatter: moneyFormatter,
        valueGetter: ({ data }) =>
          parseFloat(getS3pDataByYear(data?.s3p_data, year)?.state_fund || 0),
        aggFunc: 'jumlah',
      })),
      ...[first_year, second_year, third_year, fourth_year, fifth_year].map((year) => ({
        headerName: `PERUNTUKAN DISEMAK MOF TAHUN ${year}`,
        suppressSizeToFit: true,
        width: 350,
        cellClass: 'ag-right-aligned-cell',
        cellDataType: 'number',
        valueFormatter: moneyFormatter,
        valueGetter: ({ data }) =>
          parseFloat(getS3pDataByYear(data?.s3p_data, year)?.revised_total_allocation || 0),
        aggFunc: 'jumlah',
      })),
      ...[first_year, second_year, third_year, fourth_year, fifth_year].flatMap((year) => [
        {
          headerName: `PERBELANJAAN S3P TAHUN ${year}`,
          suppressSizeToFit: true,
          width: 350,
          cellClass: 'ag-right-aligned-cell',
          cellDataType: 'number',
          valueFormatter: moneyFormatter,
          valueGetter: ({ data }) =>
            calculateMonthlyTotal(
              getS3pDataByYear(data?.s3p_data, year)?.overall_financial_data_by_months,
              'bendahari'
            ),
          aggFunc: 'jumlah',
        },
        {
          headerName: `PERBELANJAAN TERKINI TAHUN ${year}`,
          suppressSizeToFit: true,
          width: 350,
          cellClass: 'ag-right-aligned-cell',
          cellDataType: 'number',
          valueFormatter: moneyFormatter,
          valueGetter: ({ data }) => {
            // "Perbelanjaan terkini" = lejar + terkini, matching the dashboard
            // card (OverallController: latestExpenditure = sumLejer + sumTerkini).
            const months = getS3pDataByYear(data?.s3p_data, year)?.overall_financial_data_by_months;
            return (
              calculateMonthlyTotal(months, 'lejar') + calculateMonthlyTotal(months, 'terkini')
            );
          },
          aggFunc: 'jumlah',
        },
        {
          headerName: `PERBELANJAAN SEBENAR TAHUN ${year}`,
          suppressSizeToFit: true,
          width: 350,
          cellClass: 'ag-right-aligned-cell',
          cellDataType: 'number',
          valueFormatter: moneyFormatter,
          valueGetter: ({ data }) =>
            calculateMonthlyTotal(
              getS3pDataByYear(data?.s3p_data, year)?.overall_financial_data_by_months,
              'lejar'
            ),
          aggFunc: 'jumlah',
        },
        {
          headerName: `LAPORAN PERBELANJAAN OLEH AGENSI TAHUN ${year}`,
          suppressSizeToFit: true,
          width: 400,
          cellClass: 'ag-right-aligned-cell',
          cellDataType: 'number',
          valueFormatter: moneyFormatter,
          valueGetter: ({ data }) =>
            (data?.reports ?? [])
              .filter((item) => item.year === year)
              .reduce(
                (acc, item) => acc + parseFloat(item.total_expenses_report_by_agency || 0),
                0
              ),
          aggFunc: 'jumlah',
        },
      ]),
      {
        headerName: `MAKLUMAT SILING DISEMAK ${label} (RM)`,
        suppressSizeToFit: true,
        width: 350,
        sortable: true,
        sort: 'desc',
        cellClass: 'ag-right-aligned-cell',
        cellDataType: 'number',
        valueFormatter: moneyFormatter,
        valueGetter: ({ data }) =>
          (data?.financial_details ?? []).reduce(
            (acc, item) => acc + parseFloat(item.revised_ceiling || 0),
            0
          ),
        aggFunc: 'jumlah',
      },
    ].map(adaptColDefForServerSide);
  }, [selectRmk, adaptColDefForServerSide]);

  const localeText = useMemo(() => AG_GRID_LOCALE_MS, []);

  const defaultColDef = useMemo(
    () => ({
      cellDataType: false,
      minWidth: 100,
      suppressSizeToFit: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      // Disable auto height by default for better performance
      wrapText: false,
      autoHeight: false,
      resizable: true,
      // Use simpler cell style
      cellStyle: { lineHeight: '25px' },
    }),
    []
  );

  const autoGroupColumnDef = useMemo(
    () => ({
      minWidth: 200,
      pinned: 'left',
    }),
    []
  );

  // Hide the Pivot Mode toggle + pivot drop zone — server-side endpoint
  // doesn't implement pivot, and toggling it would freeze the grid.
  const sideBarDef = useMemo(() => {
    if (!USE_SERVER_SIDE) return true;
    return {
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressPivotMode: true,
            suppressPivots: true,
            suppressValues: true,
          },
        },
        {
          id: 'filters',
          labelDefault: 'Filters',
          labelKey: 'filters',
          iconKey: 'filter',
          toolPanel: 'agFiltersToolPanel',
        },
      ],
    };
  }, []);

  const aggFuncs = useMemo(
    () => ({
      jumlah: ({ values }) =>
        values.reduce((acc, val) => acc + (typeof val === 'number' ? val : Number(val) || 0), 0),
    }),
    []
  );

  // Grid options for better performance
  const gridOptions = useMemo(
    () => ({
      // Improve scrolling performance
      suppressColumnVirtualisation: false,
      suppressRowVirtualisation: false,
      // Reduce DOM updates
      animateRows: false,
      // Batch updates
      asyncTransactionWaitMillis: 50,
      // Suppress unnecessary recalculations
      suppressPropertyNamesCheck: true,
      // Enable immutable data for better change detection
      immutableData: !USE_SERVER_SIDE,
      // For server-side group rows, params.data has no `id`. Synthesize a
      // unique id from the group level + parent keys + this row's group key.
      // Leaf rows fall back to the project id.
      getRowId: (params) => {
        if (params.data?.id != null) return params.data.id.toString();
        const level = params.level ?? 0;
        const parents = (params.parentKeys ?? []).join('|');
        // Walk the data shape used by buildGroupLevelResponse to find the key.
        const d = params.data || {};
        const groupKey =
          d.ministry?.short_form ??
          d.executing_agency?.short_form ??
          d.branch?.name ??
          d.program?.name ??
          d.main_project?.name ??
          '';
        return `g:${level}:${parents}:${groupKey}`;
      },
    }),
    []
  );

  // Server-side datasource — fetches rows in blocks of cacheBlockSize from
  // the new POST endpoint. AG Grid passes startRow/endRow/sortModel/filterModel.
  const serverSideDatasource = useMemo(() => {
    if (!USE_SERVER_SIDE) return null;
    return {
      getRows: async (params) => {
        const { startRow, endRow, sortModel, filterModel, rowGroupCols, groupKeys } =
          params.request;
        try {
          const isFederalProjects =
            JSON.parse(localStorage.getItem('federalProjects'))?.value || false;
          const { data } = await axios.post('/project-analysis-table/get-server-side', {
            rmk_id: selectRmk?.number,
            is_federal_projects: isFederalProjects,
            startRow,
            endRow,
            sortModel,
            filterModel,
            rowGroupCols,
            groupKeys,
            scope_filter: scopeFilter ?? {},
          });
          const payload = data?.data ?? data;
          params.success({
            rowData: payload?.rows ?? [],
            rowCount: payload?.lastRow ?? 0,
          });
          // Grand total only ships on first-page (startRow=0) requests at the
          // top level. Pin it to the bottom for that level. Drill-downs leave
          // the previous total in place — clear it when entering a child group.
          if (gridRef?.current?.api) {
            if (payload?.grandTotal && (groupKeys?.length ?? 0) === 0) {
              gridRef.current.api.setPinnedBottomRowData([payload.grandTotal]);
            }
          }
        } catch (e) {
          params.fail();
        }
      },
    };
  }, [selectRmk, gridRef, scopeFilter]);

  return (
    <React.Fragment>
      <div style={containerStyle}>
        <div className="grid-wrapper">
          <div style={gridStyle} className="ag-theme-alpine">
            <AgGridReact
              ref={gridRef}
              {...(USE_SERVER_SIDE
                ? {
                    rowModelType: 'serverSide',
                    serverSideDatasource,
                    serverSideStoreType: 'partial',
                    cacheBlockSize: 100,
                    maxBlocksInCache: 10,
                    blockLoadDebounceMillis: 100,
                  }
                : { rowData })}
              aggFuncs={aggFuncs}
              columnDefs={colDefs}
              excelStyles={[{ id: 'cell-wrap-text', alignment: { wrapText: true } }]}
              defaultColDef={defaultColDef}
              localeText={localeText}
              sideBar={sideBarDef}
              onGridReady={onGridReady}
              autoGroupColumnDef={autoGroupColumnDef}
              suppressDragLeaveHidesColumns={true}
              suppressMakeColumnVisibleAfterUnGroup={true}
              suppressRowGroupHidesColumns={true}
              rowGroupPanelShow="always"
              {...(USE_SERVER_SIDE
                ? {}
                : {
                    groupIncludeFooter: true,
                    groupIncludeTotalFooter: true,
                    groupTotalRow: 'bottom',
                  })}
              suppressAggFuncInHeader={true}
              // Performance optimizations
              rowBuffer={10}
              enableCellTextSelection={true}
              ensureDomOrder={true}
              // Debounced column state saving
              onColumnMoved={debouncedSaveColumnState}
              onColumnVisible={debouncedSaveColumnState}
              onColumnPinned={debouncedSaveColumnState}
              onColumnResized={debouncedSaveColumnState}
              onSortChanged={debouncedSaveColumnState}
              onColumnRowGroupChanged={debouncedSaveColumnState}
              // Additional performance settings
              {...gridOptions}
            />
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

export default React.memo(AnalysisTable);
